import { createServerFn } from '@tanstack/react-start';
import { getRequest } from '@tanstack/react-start/server';

import { getUserPortfolioById } from '~/database/supabase/portfolio/queries.server.ts';
import { getPortfolioValuePositionDataWindow } from '~/database/supabase/prices/queries.server.ts';
import type {
	CleanPortfolioValue,
	CompactPortfolioValueBreakdown,
	LegacyPortfolioValuePositionData,
	PortfolioValueBreakdown,
	PortfolioValuePositionDataRow,
} from '~/database/supabase/prices/types.ts';

import { requireStartAuthSession } from '../auth/require-start-auth-session.ts';
import { createServerFnObservabilityMiddleware } from '../utils/create-instrumented-server-fn.ts';
import { measureServerTiming } from '../utils/server-timing.ts';

export interface DetailedPortfolioHistoryWindowValue {
	readonly position_data: CompactPortfolioValueBreakdown | LegacyPortfolioValuePositionData[] | null;
	readonly timestamp: number;
}

function isRichPositionData(value: unknown): value is PortfolioValueBreakdown {
	return typeof value === 'object' && value !== null && 'version' in value && value.version === 2;
}

function isCompactPositionData(value: unknown): value is CompactPortfolioValueBreakdown {
	return typeof value === 'object' && value !== null && 'version' in value && value.version === 3;
}

function isLegacyPositionData(value: unknown): value is LegacyPortfolioValuePositionData[] {
	if (!Array.isArray(value)) {
		return false;
	}

	return value.every((entry) => typeof entry === 'object' && entry !== null && 'position_value_id' in entry);
}

function compactPositionData(
	value: CleanPortfolioValue['position_data'],
): CompactPortfolioValueBreakdown | LegacyPortfolioValuePositionData[] | null {
	if (isCompactPositionData(value)) {
		return value;
	}

	if (isRichPositionData(value)) {
		return {
			cash: {
				balance: value.cash.balance,
			},
			positions: value.positions.map((position) => ({
				chosen: position.chosen ? { close: position.chosen.close } : null,
				fx: position.fx ? { close: position.fx.close } : null,
				instrument_id: position.instrument_id,
				local: position.local ? { close: position.local.close } : null,
				position_value_id: position.position_value_id,
				shares: position.shares,
				weight_close: position.weight_close,
			})),
			totals: value.totals ? { close: value.totals.close } : null,
			version: 3,
		};
	}

	if (isLegacyPositionData(value)) {
		return value;
	}

	return null;
}

function normalizePortfolioValues(
	portfolioValues: PortfolioValuePositionDataRow[] | null | undefined,
): DetailedPortfolioHistoryWindowValue[] {
	return (
		portfolioValues?.map((portfolioValue) => ({
			position_data: compactPositionData(portfolioValue.position_data),
			timestamp: portfolioValue.timestamp,
		})) ?? []
	);
}

export const getDetailedPortfolioHistoryWindowData = createServerFn({ method: 'GET' })
	.middleware([
		createServerFnObservabilityMiddleware({
			dependencies: ['auth.session', 'supabase.portfolio.lookup', 'supabase.portfolio-values-detailed-window'],
			relatesTo: 'portfolio.history-detailed-window',
		}),
	])
	.inputValidator((data: { fromTimestamp: number; portfolioId: number; toTimestamp: number }) => data)
	.handler(async ({ data }) => {
		const request = getRequest();
		const authSession = await measureServerTiming({
			description: 'auth.session',
			name: 'portfolio-history-window-auth',
			run: () => requireStartAuthSession({ request }),
		});
		const portfolioId = Number.isFinite(data.portfolioId) ? data.portfolioId : -1;
		const fromTimestamp = Number.isFinite(data.fromTimestamp) ? Math.floor(data.fromTimestamp) : -1;
		const toTimestamp = Number.isFinite(data.toTimestamp) ? Math.floor(data.toTimestamp) : -1;

		if (portfolioId < 1 || fromTimestamp < 0 || toTimestamp < 0) {
			return [];
		}

		const portfolio = await measureServerTiming({
			description: 'supabase.portfolio.lookup',
			name: 'portfolio-history-window-portfolio',
			run: () =>
				getUserPortfolioById({
					portfolioId,
					userId: authSession.userId,
				}),
		});

		if (!portfolio) {
			return [];
		}

		const rawPortfolioValues = await measureServerTiming({
			description: 'supabase.portfolio-values-detailed-window',
			name: 'portfolio-history-window-db',
			run: () =>
				getPortfolioValuePositionDataWindow(portfolioId, {
					fromTimestamp,
					toTimestamp,
				}),
		});

		return normalizePortfolioValues(rawPortfolioValues);
	});
